import { Button, Select, Typography } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import { fetchData, getErrorMessage, Notify } from "../../utils/constants";
import { updateAdministrativUsers } from "../../utils/generalConfigurationService";
import { User } from "../../utils/types";

const SetupAdministrativSection = () => {
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const loadUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      await fetchData(
        `${process.env.REACT_APP_API_URL}/users/getAllUsers`,
        (data: User[]) => {
          setAllUsers(data);
          const currentAdministrativIds = data
            .filter((user) => user.roles?.includes("ADMINISTRATIV"))
            .map((user) => user.id);
          setSelectedUserIds(currentAdministrativIds);
        },
      );
    } catch (error) {
      console.error("Failed to load users for Setup Administrativ:", error);
      Notify("error", "Could not load users.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const userOptions = useMemo(
    () =>
      allUsers.map((user) => ({
        value: user.id,
        label: `${user.firstName} ${user.lastName} (${user.email})`,
      })),
    [allUsers],
  );

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await updateAdministrativUsers(selectedUserIds);
      Notify("success", "Administrativ users updated successfully.");
    } catch (error) {
      console.error("Failed to update Administrativ users:", error);
      Notify(
        "error",
        getErrorMessage(error, "Could not update Administrativ users."),
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="section-container" style={{ marginTop: 16 }}>
      <Typography.Title level={4}>Setup Administrativ</Typography.Title>
      <Typography.Paragraph type="secondary">
        Select the users who can act as Administrativ approvers for leave
        requests.
      </Typography.Paragraph>
      <Select
        mode="multiple"
        style={{ width: "100%" }}
        placeholder="Select Administrativ users"
        value={selectedUserIds}
        onChange={setSelectedUserIds}
        options={userOptions}
        loading={isLoading}
        disabled={isLoading || isSaving}
        optionFilterProp="label"
        showSearch
      />
      <Button
        type="primary"
        style={{ marginTop: 12 }}
        onClick={handleSave}
        loading={isSaving}
        disabled={isLoading}
      >
        Save Administrativ users
      </Button>
    </div>
  );
};

export default SetupAdministrativSection;
